import { useState, useEffect, useCallback } from 'react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import { useAuthStore } from '@/stores/auth';
import {
  CreditCard, CheckCircle, Loader2, ArrowRight, Crown,
  Sparkles, Users, HardDrive, Headphones, Package,
  AlertCircle,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Plan {
  id: string;
  name: string;
  amountINR: number;
  amountPaise: number;
  features: string[];
}

interface CurrentBilling {
  plan: string;
  planName: string;
  status: string;
  activatedAt: string | null;
  expiresAt: string | null;
  lastTransactionId: string | null;
}

/* ------------------------------------------------------------------ */
/*  Plan tier metadata                                                 */
/* ------------------------------------------------------------------ */

const TIER_ORDER: Record<string, number> = {
  starter: 0,
  growth: 1,
  business: 2,
  enterprise: 3,
};

// BUG-19 fix (May 1 2026): replaced bright blue/purple gradients with warm-ink
// design tokens (forest = #2D5A3D, saffron = #C98A3B) matching the AURA palette.
const PLAN_COLORS: Record<string, { gradient: string; badge: string; border: string }> = {
  starter:    { gradient: 'from-slate-500 to-slate-600',           badge: 'bg-slate-500/10 text-slate-400 border-slate-500/20',   border: 'border-slate-500/30' },
  growth:     { gradient: 'from-[#2D5A3D] to-[#3a7550]',          badge: 'bg-[#2D5A3D]/10 text-[#2D5A3D] border-[#2D5A3D]/20',  border: 'border-[#2D5A3D]/30' },
  business:   { gradient: 'from-[#C98A3B] to-[#B87A2E]',          badge: 'bg-[#C98A3B]/10 text-[#C98A3B] border-[#C98A3B]/20',  border: 'border-[#C98A3B]/30' },
  enterprise: { gradient: 'from-amber-500 to-orange-500',          badge: 'bg-amber-500/10 text-amber-400 border-amber-500/20',   border: 'border-amber-500/30' },
};

const PLAN_ICONS: Record<string, typeof Package> = {
  starter: Package,
  growth: Users,
  business: Sparkles,
  enterprise: Crown,
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Map backend plan enum (STARTER, GROWTH, ENTERPRISE) to plan id */
function planEnumToId(plan: string): string {
  const lower = plan.toLowerCase();
  if (lower === 'starter') return 'starter';
  if (lower === 'growth') return 'growth';
  // Business and Enterprise both map to ENTERPRISE enum in backend
  return lower;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function BillingPlansPage() {
  const { user } = useAuthStore();
  const isAdmin = user?.role?.name === 'admin';

  const [plans, setPlans] = useState<Plan[]>([]);
  const [current, setCurrent] = useState<CurrentBilling | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initiating, setInitiating] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [plansRes, currentRes] = await Promise.all([
        api.get('/billing/plans'),
        api.get('/billing/current').catch(() => null),
      ]);
      if (plansRes.data?.data) setPlans(plansRes.data.data);
      if (currentRes?.data?.data) setCurrent(currentRes.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load billing plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpgrade = async (planId: string) => {
    try {
      setInitiating(planId);
      setError(null);
      const res = await api.post('/billing/initiate', { planId });
      const paymentUrl = res.data?.data?.paymentUrl;
      if (paymentUrl) {
        window.location.href = paymentUrl;
      } else {
        setError('Payment URL not received. Please try again.');
        setInitiating(null);
      }
    } catch (err: any) {
      // Sanitize raw PhonePe error messages — they can contain internal codes
      // like "PhonePe OAuth failed: 401 {..." which are not user-friendly.
      const raw: string = err.response?.data?.error || '';
      let friendly = raw;
      if (raw.toLowerCase().includes('oauth') || raw.toLowerCase().includes('401') || raw.toLowerCase().includes('credential')) {
        friendly = 'Payment gateway is temporarily unavailable. Please try again later or contact support.';
      } else if (!raw) {
        friendly = 'Failed to initiate payment. Please try again.';
      }
      setError(friendly);
      setInitiating(null);
    }
  };

  /* ---- Determine current plan tier ---- */
  const currentPlanName = current?.planName?.toLowerCase() || 'starter';
  const currentTier = TIER_ORDER[currentPlanName] ?? 0;

  /* ---- Loading ---- */
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-[var(--text-muted)]">Loading billing plans...</span>
      </div>
    );
  }

  /* ---- Error ---- */
  if (error && plans.length === 0) {
    return (
      <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-center">
        <p className="text-sm text-red-400">{error}</p>
        <button onClick={fetchData} className="mt-2 text-sm text-[var(--saffron-600)] hover:underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500">
            <CreditCard size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">Subscription Plans</h1>
            <p className="text-sm text-[var(--text-muted)]">
              Choose the plan that fits your business needs
            </p>
          </div>
        </div>
      </div>

      {/* Current Plan Banner */}
      {current && (
        <div className="mb-6 rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10">
                <CheckCircle size={20} className="text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-[var(--text-muted)]">Current Plan</p>
                <p className="text-lg font-bold text-[var(--text-primary)]">{current.planName}</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-xs text-[var(--text-muted)]">Status</p>
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  current.status === 'active'
                    ? 'bg-emerald-500/10 text-emerald-400'
                    : 'bg-amber-500/10 text-amber-400'
                }`}>
                  {current.status === 'active' ? 'Active' : current.status}
                </span>
              </div>
              {current.activatedAt && (
                <div className="text-right">
                  <p className="text-xs text-[var(--text-muted)]">Activated</p>
                  <p className="text-sm font-medium text-[var(--text-secondary)]">
                    {new Date(current.activatedAt).toLocaleDateString('en-IN')}
                  </p>
                </div>
              )}
              {current.expiresAt && (
                <div className="text-right">
                  <p className="text-xs text-[var(--text-muted)]">Expires</p>
                  <p className="text-sm font-medium text-[var(--text-secondary)]">
                    {new Date(current.expiresAt).toLocaleDateString('en-IN')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error banner (non-fatal) */}
      {error && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3">
          <AlertCircle size={16} className="shrink-0 text-red-400" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Plans Grid — enterprise is rendered as a separate "Talk to us" card
          below, so the backend never exposes its price. */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {plans.map((plan) => {
          const planTier = TIER_ORDER[plan.id] ?? 0;
          const isCurrent = currentPlanName === plan.id;
          const isDowngrade = planTier < currentTier;
          const isUpgrade = planTier > currentTier;
          const colors = PLAN_COLORS[plan.id] || PLAN_COLORS.starter!;
          const PlanIcon = PLAN_ICONS[plan.id] || Package;

          return (
            <div
              key={plan.id}
              className={`relative flex flex-col rounded-xl border bg-[var(--bg-surface)] transition-all ${
                isCurrent
                  ? `${colors.border} ring-2 ring-[var(--ink-0)]/20`
                  : 'border-[var(--border-primary)] hover:border-[var(--border-secondary)]'
              }`}
            >
              {/* Current badge */}
              {isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-[var(--ink-0)] px-3 py-1 text-xs font-semibold text-white shadow-sm">
                    <CheckCircle size={12} />
                    Current Plan
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="p-6 pb-4">
                <div className="mb-4 flex items-center gap-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${colors.gradient}`}>
                    <PlanIcon size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[var(--text-primary)]">{plan.name}</h3>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-[var(--text-primary)]">
                      {formatINR(plan.amountINR, false)}
                    </span>
                    <span className="text-sm text-[var(--text-muted)]">/month</span>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-2.5">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle size={16} className="mt-0.5 shrink-0 text-emerald-400" />
                      <span className="text-sm text-[var(--text-secondary)]">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Button */}
              <div className="mt-auto border-t border-[var(--border-primary)] p-6 pt-4">
                {isCurrent ? (
                  <button
                    disabled
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--border-primary)] px-4 py-2.5 text-sm font-medium text-[var(--text-muted)] cursor-not-allowed"
                  >
                    <CheckCircle size={16} />
                    Current Plan
                  </button>
                ) : isDowngrade ? (
                  <button
                    disabled
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--border-primary)] px-4 py-2.5 text-sm font-medium text-[var(--text-muted)] cursor-not-allowed"
                  >
                    Downgrade not available
                  </button>
                ) : isAdmin ? (
                  <button
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={initiating !== null}
                    className={`flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r ${colors.gradient} px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all`}
                  >
                    {initiating === plan.id ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Redirecting to PhonePe...
                      </>
                    ) : (
                      <>
                        {isUpgrade ? 'Upgrade' : 'Select Plan'}
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                ) : (
                  <p className="text-center text-xs text-[var(--text-muted)]">
                    Contact your admin to upgrade
                  </p>
                )}
              </div>
            </div>
          );
        })}

        {/* Enterprise — Talk to us (price never published per pricing strategy) */}
        <div className="relative flex flex-col rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-orange-500/5 transition-all hover:border-amber-500/50">
          <div className="p-6 pb-4">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-500">
                <Crown size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[var(--text-primary)]">Enterprise</h3>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-[var(--text-primary)]">Talk to us</span>
              </div>
              <p className="mt-1 text-xs text-[var(--text-muted)]">Bespoke pricing for your organisation</p>
            </div>

            <ul className="space-y-2.5">
              {[
                'Unlimited users & workspaces',
                'All modules + custom integrations',
                'AI assistant (unlimited credits)',
                'Dedicated account manager',
                '24/7 SLA support',
                'Custom SSO, audit logs, data residency',
              ].map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-0.5 shrink-0 text-amber-400" />
                  <span className="text-sm text-[var(--text-secondary)]">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-auto border-t border-amber-500/20 p-6 pt-4">
            <a
              href="mailto:sales@ardhanex.com?subject=AURA%20Enterprise%20Inquiry"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:opacity-90 transition-all"
            >
              Contact Sales
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>

      {/* Footer note */}
      <div className="mt-8 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4">
        <div className="flex items-start gap-3">
          <CreditCard size={18} className="mt-0.5 shrink-0 text-[var(--text-muted)]" />
          <div>
            <p className="text-sm font-medium text-[var(--text-secondary)]">Secure payments via PhonePe</p>
            <p className="text-xs text-[var(--text-muted)]">
              All payments are processed securely through PhonePe's payment gateway. Plans are billed monthly
              and activate immediately upon successful payment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
